<template>
  <div>
    <EquipmentEditHeader
      v-if="isAdmin"
      data-cy="hps-bolt-on-discharge-search-view-edit-header"
    />
    <div v-else class="mb-3">
      <div class="flex align-items-center field-checkbox">
        <Checkbox
          v-model="enableFilter"
          input-id="enable-hps-bolt-on-discharge-filter"
          binary
        />
        <label for="enable-hps-bolt-on-discharge-filter">
          Filter and Sort Discharge
        </label>
      </div>
    </div>
    <div
      v-if="showPumpRequiredWarning"
      class="no-recommendation-message-container"
      data-cy="hps-bolt-on-discharge-pump-required-warning"
    >
      <div class="no-recommendation-message">
        <p class="no-recommendation-heading">
          Select a pump first to get discharge recommendations.
        </p>
        <p class="no-recommendation-body">
          To browse all discharges, un-check
          <span class="no-recommendation-highlight">
            &quot;Filter and Sort Discharge&quot;</span
          >.
        </p>
      </div>
    </div>
    <div
      v-else-if="showNoMatchingDischargesWarning"
      class="no-recommendation-message-container"
      data-cy="hps-bolt-on-discharge-no-matches-warning"
    >
      <div class="no-recommendation-message">
        <p class="no-recommendation-heading">
          No discharges match the pump outer diameter and the 20 ft/s velocity
          limit.
        </p>
        <p class="no-recommendation-body">
          To browse all discharges, un-check
          <span class="no-recommendation-highlight">
            &quot;Filter and Sort Discharge&quot;</span
          >.
        </p>
      </div>
    </div>
    <EquipmentTable
      data-cy="hps-bolt-on-discharge-search-view-table"
      @select="$emit('select', $event.data as Bolt_On_Discharges)"
    />
  </div>
</template>
<script setup lang="ts">
import { computed, provide, ref } from "vue";
import Checkbox from "primevue/checkbox";
import { useHpsBoltOnDischarges } from "@/composables/equipment/hpsBoltOnDischarges/hpsBoltOnDischarges";
import EquipmentEditHeader from "@/components/equipment/EquipmentEditHeader.vue";
import EquipmentTable from "@/components/equipment/EquipmentTable.vue";
import type { Bolt_On_Discharges } from "gql/api";
import { injectStrict } from "@/utils/utils";
import { scenarioCalculationsKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import type { EquipmentTableColumn } from "@/composables/equipment/equipmentTypes";
import {
  calculateCircularFlowVelocityFtSec,
  HPS_DISCHARGE_VELOCITY_LIMIT_FT_SEC
} from "@/libs/calculations/flowVelocity";
import { matchesAnyDischargeOuterDiameter } from "@/models/equipment/bolt_on_discharges/properties";

const props = defineProps<{
  isAdmin: boolean;
}>();

defineEmits<{
  (event: "select", data: Bolt_On_Discharges): void;
}>();

const hpsBoltOnDischargeSearch = useHpsBoltOnDischarges(props.isAdmin);

const scenarioCalculations = !props.isAdmin
  ? injectStrict(scenarioCalculationsKey)
  : null;
const enableFilter = ref(!props.isAdmin);

const flowRateAtDischargeBBLD = computed(() => {
  const context =
    scenarioCalculations?.fluidPropertiesAfterPumpDischargeTubingContext.value;
  return context?.flowRatesInSitu?.fluidFlowRateBBLD || 0;
});

/**
 * The bolt-on discharge is matched against the bottom pump housing, which is
 * the pump HPS Suction matches too. Keeping both on the same pump keeps one
 * scenario from recommending suction and discharge off different housings.
 */
const pumpHousingOuterDiameterInches = computed(() => {
  if (!scenarioCalculations) return null;
  return (
    scenarioCalculations.bottomPump.value?.r_pump
      ?.housing_outer_diameter_inches ?? null
  );
});

const isFilterActive = computed(() => !props.isAdmin && enableFilter.value);

function getDischargeVelocityFtSec(
  discharge: Bolt_On_Discharges
): number | null {
  return calculateCircularFlowVelocityFtSec(
    flowRateAtDischargeBBLD.value,
    discharge.inner_diameter_inches
  );
}

const sortedDischarges = computed(() => {
  const rows = hpsBoltOnDischargeSearch.allRows.value;
  if (!rows || !isFilterActive.value) return rows;

  const pumpOuterDiameter = pumpHousingOuterDiameterInches.value;
  if (pumpOuterDiameter === null) return [];

  const matchingRows = rows.filter(row =>
    matchesAnyDischargeOuterDiameter(row, pumpOuterDiameter)
  );
  if (matchingRows.length === 0) return [];

  const withVelocity = matchingRows
    .map(row => ({ row, velocity: getDischargeVelocityFtSec(row) }))
    .filter(
      (entry): entry is { row: Bolt_On_Discharges; velocity: number } =>
        entry.velocity !== null
    );
  const withoutVelocity = matchingRows.filter(
    row => !withVelocity.some(entry => entry.row.id === row.id)
  );

  return [
    ...withVelocity
      .filter(entry => entry.velocity <= HPS_DISCHARGE_VELOCITY_LIMIT_FT_SEC)
      .sort((a, b) => {
        const diameterDifference =
          Number(a.row.inner_diameter_inches) -
          Number(b.row.inner_diameter_inches);
        return diameterDifference || a.velocity - b.velocity;
      })
      .map(entry => entry.row),
    ...withoutVelocity
  ];
});

const sortedFilteredRows = computed(() => {
  const baseFiltered = hpsBoltOnDischargeSearch.filteredRows.value;
  const sorted = sortedDischarges.value;
  if (!baseFiltered || !sorted) return sorted as typeof baseFiltered;

  const filteredIds = new Set(baseFiltered.map(row => row.id));
  return sorted.filter(row => filteredIds.has(row.id));
});

const showPumpRequiredWarning = computed(
  () => isFilterActive.value && pumpHousingOuterDiameterInches.value === null
);

const showNoMatchingDischargesWarning = computed(() => {
  const sourceRows = hpsBoltOnDischargeSearch.allRows.value;
  const rows = sortedDischarges.value;
  return (
    isFilterActive.value &&
    pumpHousingOuterDiameterInches.value !== null &&
    Array.isArray(sourceRows) &&
    sourceRows.length > 0 &&
    Array.isArray(rows) &&
    rows.length === 0
  );
});

const equipmentWithSortedDischarges = {
  ...hpsBoltOnDischargeSearch,
  columns: computed(() => {
    const columnsRaw = hpsBoltOnDischargeSearch.columns;
    const baseColumns = Array.isArray(columnsRaw)
      ? (columnsRaw as EquipmentTableColumn<Bolt_On_Discharges>[])
      : (columnsRaw.value as EquipmentTableColumn<Bolt_On_Discharges>[]);
    if (props.isAdmin) return baseColumns;

    const flowrateColumn: EquipmentTableColumn<Bolt_On_Discharges> = {
      field: "flowrate_ft_sec" as unknown as keyof Bolt_On_Discharges,
      header: "Flowrate (ft/s)",
      isNumber: true,
      sortable: true,
      decimalPlaces: 4,
      editable: false,
      format: value => {
        if (value === null || value === undefined) return "";
        const numberValue = Number(value);
        return Number.isNaN(numberValue) ? "" : numberValue.toFixed(4);
      }
    };
    const innerDiameterIndex = baseColumns.findIndex(
      column => column.field === "inner_diameter_inches"
    );
    const insertionIndex =
      innerDiameterIndex < 0 ? baseColumns.length : innerDiameterIndex + 1;
    return [
      ...baseColumns.slice(0, insertionIndex),
      flowrateColumn,
      ...baseColumns.slice(insertionIndex)
    ];
  }),
  allRows: computed(() => {
    if (props.isAdmin) return hpsBoltOnDischargeSearch.allRows.value;
    return sortedDischarges.value?.map(row => ({
      ...row,
      flowrate_ft_sec: getDischargeVelocityFtSec(row)
    }));
  }),
  filteredRows: computed(() => {
    if (props.isAdmin) return hpsBoltOnDischargeSearch.filteredRows.value;
    return sortedFilteredRows.value?.map(row => ({
      ...row,
      flowrate_ft_sec: getDischargeVelocityFtSec(row)
    }));
  })
};

provide("equipment", equipmentWithSortedDischarges);
</script>
